import * as ents from '@attain-apps/entities'
import * as store from '@attain-apps/fe/web/main/lib/store'
import * as rxjs from 'rxjs'

import * as configuration from '../../configuration'
import type { Plugin } from '../plugin'

/**
 * Requests the currently open report be generated via
 * `generateSpaceReportFile`. Generation runs in the background — see
 * {@link showToastOnDownloadSuccess} for what happens once the request is
 * accepted
 */
export const downloadOnRequest = store.effects.create(
  // NOTE: Types will be inferred from feature when registered
  (feature: Plugin['feature'], plugin: Plugin) =>
    feature.actions.download.request.observe().pipe(
      rxjs.withLatestFrom(
        plugin.collection.feature.selectors.context.asObservable,
        plugin.collection.plugins.currentEntityPlugin.feature.selectors.getId()
          .asObservable,
      ),
      rxjs.mergeMap(([action, context, reportId]) => {
        if (!ents.core.admin.Context.ForScenario.isForScenario(context)) {
          return rxjs.of(
            feature.actions.download.error.from(action).create({
              error: new Error('No scenario context to download a report in'),
            }),
          )
        }
        if (!ents.extra.space.Report.Id.isId(reportId)) {
          return rxjs.of(
            feature.actions.download.error.from(action).create({
              error: new Error('No report to download'),
            }),
          )
        }

        return plugin.collection.plugins
          .lookup(configuration.Plugin)
          .get()
          .feature.selectors.getConfiguration(reportId)
          .asObservable.pipe(
            rxjs.take(1),
            rxjs.switchMap(_configuration =>
              plugin.api
                .generate(context, {
                  configuration: _configuration,
                  id: reportId,
                })
                .pipe(
                  rxjs.map(() =>
                    feature.actions.download.success
                      .from(action)
                      .create({ response: undefined }),
                  ),
                ),
            ),
            rxjs.catchError((error: Error) =>
              rxjs.of(
                feature.actions.download.error.from(action).create({ error }),
              ),
            ),
          )
      }),
    ),
)
